import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Search } from "lucide-react";
import { StockItem } from "@/types/stock-item";

interface AddItemsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedItemCodes: string[]; // Already selected items
  allItems: StockItem[]; // All available stock items
  onAddItems: (items: StockItem[]) => void;
}

export function AddItemsDialog({
  open,
  onOpenChange,
  selectedItemCodes,
  allItems,
  onAddItems,
}: AddItemsDialogProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  // Filter out already-selected items
  const availableItems = allItems.filter(
    (item) => !selectedItemCodes.includes(item.code)
  );

  // Filter by search query
  const filteredItems = availableItems.filter(
    (item) =>
      item.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleToggleItem = (itemCode: string) => {
    setSelectedItems((prev) =>
      prev.includes(itemCode)
        ? prev.filter((code) => code !== itemCode)
        : [...prev, itemCode]
    );
  };

  const handleSelectAll = () => {
    setSelectedItems(filteredItems.map((item) => item.code));
  };

  const handleDeselectAll = () => {
    setSelectedItems([]);
  };

  const handleAddItems = () => {
    const itemsToAdd = allItems.filter((item) =>
      selectedItems.includes(item.code)
    );
    onAddItems(itemsToAdd);
    onOpenChange(false);
    setSelectedItems([]);
    setSearchQuery("");
  };

  const handleCancel = () => {
    onOpenChange(false);
    setSelectedItems([]);
    setSearchQuery("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Add More Items</DialogTitle>
          <DialogDescription>
            Select additional items to add to your transfer
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-4 overflow-hidden flex flex-col">
          {/* Search and Actions */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by code or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSelectAll}
              disabled={filteredItems.length === 0}
            >
              Select All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDeselectAll}
              disabled={selectedItems.length === 0}
            >
              Deselect All
            </Button>
          </div>

          {/* Items List */}
          <ScrollArea className="flex-1 border rounded-md">
            <div className="p-4 space-y-2">
              {filteredItems.length > 0 ? (
                filteredItems.map((item) => (
                  <div
                    key={item.code}
                    className="flex items-start gap-3 p-3 border rounded-lg hover:bg-accent cursor-pointer transition-colors"
                    onClick={() => handleToggleItem(item.code)}
                  >
                    <Checkbox
                      checked={selectedItems.includes(item.code)}
                      onCheckedChange={() => handleToggleItem(item.code)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">
                            {item.code} - {item.description}
                          </div>
                          <div className="text-sm text-muted-foreground mt-1">
                            Available: {item.physicalQuantity} {item.unit}
                          </div>
                        </div>
                        {item.status === "Active" && (
                          <Badge variant="secondary" className="shrink-0">
                            Active
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-muted-foreground py-12">
                  {searchQuery ? (
                    <div>
                      <p className="text-lg font-medium">No items found</p>
                      <p className="text-sm mt-1">
                        Try adjusting your search query
                      </p>
                    </div>
                  ) : availableItems.length === 0 ? (
                    <div>
                      <p className="text-lg font-medium">All items already added</p>
                      <p className="text-sm mt-1">
                        You've added all available stock items to this transfer
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-lg font-medium">No items available</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Selected Count */}
          {selectedItems.length > 0 && (
            <div className="flex items-center justify-between px-1">
              <div className="text-sm text-muted-foreground">
                Selected: <span className="font-medium">{selectedItems.length}</span> item(s)
              </div>
              {filteredItems.length > 0 && (
                <div className="text-sm text-muted-foreground">
                  Available: <span className="font-medium">{filteredItems.length}</span> item(s)
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleAddItems} disabled={selectedItems.length === 0}>
            Add Items ({selectedItems.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
